import styles from "./LeftPanel.module.css";
import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import ColumnButton from "../../components/ColumnButton";
import { Datasets, OTHER_DATASET_NAME } from "../../constants";
import SectionTitle from "../../components/SectionTitle";
import Select from "../../components/Select";
import { TRootState } from "../../store/create-store";
import { useDispatch, useSelector } from "react-redux";
import { rootActions } from "../../reducers/root.slice";
import debounce from "lodash.debounce";
import ColumnUtils from "../../utils/ColumnUtils";
import ColumnSection from "./ColumnSection";
import { TColumnName } from "../../types";
import ColumnNameUtils from "../../utils/ColumnNameUtils";
import { selectLeftPanelState, selectRootState } from "../../selectors";

type TProps = {};

type TDatasetSelectorProps = {
  selectedDataset: string;
  showFileUpload: boolean;
  onDatasetChange: (dataset: string) => void;
  onFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
};

const DatasetSelector: FunctionComponent<TDatasetSelectorProps> = (props) => {
  return (
    <>
      <SectionTitle>Dataset</SectionTitle>

      <Select
        selected={props.selectedDataset}
        placeholder="Select dataset"
        onChange={props.onDatasetChange}
        options={Datasets.map((dataset) => dataset.name)}
      />

      {props.showFileUpload && (
        <input type="file" accept="text/csv" onChange={props.onFileSelect} />
      )}
    </>
  );
};

const LeftPanel: FunctionComponent<TProps> = () => {
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
  const state = useSelector(selectLeftPanel);
  const dispatch = useDispatch();
  let categoricalPredictionColumns = state.categoricalPredictionColumns;
  let continuousPredictionColumns = state.continuousPredictionColumns;

  if (state.selectedMethod) {
    const method: TColumnName = state.selectedMethod;
    categoricalPredictionColumns = ColumnUtils.getPredictionColumnsForMethod(
      categoricalPredictionColumns,
      method
    );
    continuousPredictionColumns = ColumnUtils.getPredictionColumnsForMethod(
      continuousPredictionColumns,
      method
    );
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const currentFocusedElement = document.activeElement;
      if (
        currentFocusedElement &&
        ["input", "select", "textarea"].includes(
          currentFocusedElement.tagName.toLowerCase()
        ) &&
        currentFocusedElement.getAttribute("data-component") !== "Switch"
      ) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          dispatch(rootActions.loadPrevOrNextColumnSaga("prev"));
          break;

        case "ArrowRight":
          dispatch(rootActions.loadPrevOrNextColumnSaga("next"));
          break;

        case "ArrowUp":
          dispatch(rootActions.loadPrevOrNextMethod("prev"));
          break;

        case "ArrowDown":
          dispatch(rootActions.loadPrevOrNextMethod("next"));
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const changeColumnsForHover = (column: TColumnName) => {
    const newColumnsForHover = state.columnsForHover.includes(column)
      ? state.columnsForHover.filter((c) => c !== column)
      : [...state.columnsForHover, column];

    dispatch(rootActions.setColumnsForHover(newColumnsForHover));
  };

  const debouncedGenerateGraph = debounce(() => {
    dispatch(rootActions.generateGraph());
  }, 100);

  useEffect(() => {
    dispatch(rootActions.initialise(Datasets[0].name));
  }, []);

  // useEffectDebugger (
  useEffect(
    debouncedGenerateGraph,
    [
      state.selectedDataset,
      state.selectedMethod,
      state.selectedColumn,
      state.columnsForHover,
    ]
    // [
    //   "state.selectedDataset",
    //   "state.selectedMethod",
    //   "state.selectedColumn",
    //   "state.columnsForHover",
    // ]
  );

  const onDatasetChange = (value: string) => {
    if (value === OTHER_DATASET_NAME) {
      setShowFileUpload(true);
      dispatch(rootActions.unselectDataset());
      dispatch(rootActions.setSelectedDataset(value));
    } else {
      setShowFileUpload(false);
      dispatch(rootActions.initialise(value));
    }
  };

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      dispatch(rootActions.loadDatasetFromFile(file));
    } else {
      console.warn("No file selected");
    }
  };

  const selectedDataset =
    Datasets.find((d) => d.name === state.selectedDataset)?.name ??
    OTHER_DATASET_NAME;

  const datasetSelector = (
    <DatasetSelector
      selectedDataset={selectedDataset}
      onDatasetChange={onDatasetChange}
      showFileUpload={showFileUpload}
      onFileSelect={onFileSelect}
    />
  );

  if (state.selectedDataset === OTHER_DATASET_NAME) {
    return (
      <section className={styles.wrapper}>
        <div className={styles.innerWrapper}>{datasetSelector}</div>
      </section>
    );
  }

  return (
    <section className={styles.wrapper}>
      <div className={styles.innerWrapper}>
        {datasetSelector}

        <SectionTitle>Method</SectionTitle>

        <div className={styles.columns}>
          {state.methodColumns.map((method) => (
            <ColumnButton
              key={method}
              isActive={state.selectedMethod === method}
              onClick={() => {
                if (
                  state.selectedColumn &&
                  ColumnUtils.isPredictionColumn(
                    state.selectedColumn,
                    categoricalPredictionColumns,
                    continuousPredictionColumns
                  )
                ) {
                  dispatch(
                    rootActions.setSelectedMethodAndColumn({
                      method,
                      column: ColumnUtils.getDefaultColumn({
                        selectedMethod: method,
                        categoricalColumns: state.categoricalColumns,
                        categoricalPredictionColumns:
                          state.categoricalPredictionColumns,
                        continuousPredictionColumns:
                          state.continuousPredictionColumns,
                      }),
                    })
                  );
                } else {
                  dispatch(rootActions.setSelectedMethod(method));
                }
              }}
            >
              {ColumnNameUtils.getLabelForMethodX(method)}
            </ColumnButton>
          ))}
        </div>

        <SectionTitle>Column</SectionTitle>

        {(categoricalPredictionColumns.length > 0 ||
          continuousPredictionColumns.length > 0) && (
          <>
            <span className={styles.sectionSubTitle}>
              Prediction (
              {categoricalPredictionColumns.length +
                continuousPredictionColumns.length}
              )
            </span>

            {state.selectedMethod ? (
              <ColumnSection
                columns={categoricalPredictionColumns}
                selectedColumn={state.selectedColumn}
                render={(column) =>
                  state.selectedMethod
                    ? ColumnNameUtils.getPredictionLabel(
                        column,
                        state.selectedMethod
                      )
                    : column
                }
                onClick={(column) =>
                  dispatch(rootActions.setSelectedColumn(column))
                }
              />
            ) : (
              "No method selected"
            )}

            {state.selectedMethod ? (
              <ColumnSection
                columns={continuousPredictionColumns}
                selectedColumn={state.selectedColumn}
                render={(column) =>
                  state.selectedMethod
                    ? ColumnNameUtils.getPredictionLabel(
                        column,
                        state.selectedMethod
                      )
                    : column
                }
                onClick={(column) =>
                  dispatch(rootActions.setSelectedColumn(column))
                }
              />
            ) : (
              "No method selected"
            )}
          </>
        )}

        <span className={styles.sectionSubTitle}>
          Categorical variables ({state.categoricalColumns.length})
        </span>

        <ColumnSection
          columns={state.categoricalColumns}
          selectedColumn={state.selectedColumn}
          onClick={(column) => dispatch(rootActions.setSelectedColumn(column))}
        />

        <span className={styles.sectionSubTitle}>
          Continuous variables ({state.continuousColumns.length})
        </span>

        <ColumnSection
          columns={state.continuousColumns}
          selectedColumn={state.selectedColumn}
          onClick={(column) => dispatch(rootActions.setSelectedColumn(column))}
        />

        <SectionTitle>Hover info</SectionTitle>

        <div className={styles.checkboxes}>
          {ColumnUtils.combine(
            {
              categoricalColumns: state.categoricalColumns,
              continuousColumns: state.continuousColumns,
              categoricalPredictionColumns: state.categoricalPredictionColumns,
              continuousPredictionColumns: state.continuousPredictionColumns,
              selectedMethod: state.selectedMethod,
            },
            true
          ).map((column) => (
            <label key={column} className={styles.checkbox}>
              <input
                name={column}
                type="checkbox"
                checked={
                  state.selectedColumn === column ||
                  state.columnsForHover.includes(column)
                }
                disabled={state.selectedColumn === column}
                onChange={() => changeColumnsForHover(column)}
              />
              {column}
            </label>
          ))}
        </div>
      </div>
    </section>
  );
};

const selectLeftPanel = (state: TRootState) => {
  const {
    categoricalColumns,
    continuousColumns,
    methodColumns,
    categoricalPredictionColumns,
    continuousPredictionColumns,
  } = selectRootState(state);
  const leftPanel = selectLeftPanelState(state);

  return {
    selectedDataset: leftPanel.selectedDataset,
    selectedMethod: leftPanel.selectedMethod,
    selectedColumn: leftPanel.selectedColumn,
    columnsForHover: leftPanel.columnsForHover,
    categoricalColumns,
    continuousColumns,
    methodColumns,
    categoricalPredictionColumns,
    continuousPredictionColumns,
  };
};

export default LeftPanel;
